import React, { useEffect, useState } from 'react';
import { Modal, Button, Typography, Divider, Row, Col } from 'antd';
import '../Atoms/Nominee.css';
import { AwardsName } from '../Atoms/AwardsName';

const { Text, Paragraph } = Typography;

interface NomineeDetailsModalProps {
    visible: boolean;
    onCancel: () => void;
    empData: any;
}

const NomineeDetailsModal: React.FC<NomineeDetailsModalProps> = ({ visible, onCancel, empData }) => {
    const [parsedData, setParsedData] = useState<Array<[string, string[]]>>([]);

    useEffect(() => {
        if (visible && empData) {
            const parseNominatorCitation = (citation: string) => {
                const sections: string[] = [];
                let lastIndex = 0;

                for (let i = 0; i < citation.length; i++) {
                    if (citation[i].match(/\d/) && citation.slice(i).match(/^\d+\)\.?\s+/)) {
                        const matchLength = citation.slice(i).match(/^\d+\)\.?\s+/)![0].length;
                        const part = citation.slice(lastIndex, i).trim();
                        if (part) {
                            sections.push(part);
                        }
                        lastIndex = i + matchLength;
                        i = lastIndex - 1;
                    }
                }

                const lastPart = citation.slice(lastIndex).trim();
                if (lastPart) {
                    sections.push(lastPart);
                }

                return sections;
            };

            const splitData = parseNominatorCitation(empData.nominatorCitation);

            const parseSubItems = (text: string) => {
                return text.split(/^\d+\.\s+/).filter(item => item.trim() !== '');
            };

            const structuredData: [string, string[]][] = splitData.map((section: string, index: number) => {
                if (index === 3) {
                    return ['Nomination Year:', ['H1 2026']];
                }
                const [title, ...rest] = section.split(': ');
                const content = rest.join(': ');
                const subItems = parseSubItems(content);
                return [title.trim() + ':', subItems.length > 0 ? subItems : [content.trim()]];
            });

            setParsedData(structuredData);
        }
    }, [visible, empData]);

    const getInitials = (name: string) => {
        const [firstName, lastName] = name.split(' ');
        if (!firstName || !lastName) {
            return '';
        }
        return `${firstName.charAt(0)}${lastName.charAt(0)}`;
    };

    const handleCancel = () => {
        onCancel();
    };

    return (
        <Modal
            title="Nominee Details"
            open={visible}
            onCancel={handleCancel}
            footer={[
                <Button key="cancel" onClick={handleCancel}>
                    Cancel
                </Button>,
            ]}
            width={1000}
        >
            {empData ? (
                <>
                    <div className="nominee-header">
                        {empData.rewardName === AwardsName.IndividualAwards ? (
                            <div className="initials-circle">
                                <Text strong className="initials-text">{getInitials(empData.nomineeName)}</Text>
                            </div>
                        ) : null}

                        <div className="nominee-details">
                            {empData.rewardName === AwardsName.IndividualAwards ? (
                                <div>
                                    <Text strong>{empData.nomineeName}</Text><br />
                                    <Text type="secondary">{empData.nomineeDesignation} | {empData.nomineeUnit}</Text>
                                </div>
                            ) : null}
                            {empData.rewardName === AwardsName.TeamAwards ? (
                                <div>
                                    <Text strong>{empData.teamName}</Text><br />
                                </div>
                            ) : null}
                        </div>
                    </div>
                    <Divider />
                    <Row gutter={[16, 16]}>
                        {empData.rewardName === AwardsName.IndividualAwards ? (
                            <>
                                <Col span={8}>
                                    <Text strong>Manager</Text> <br />
                                    {empData.managerName}
                                </Col>
                                <Col span={8}>
                                    <Text strong>Business Unit</Text> <br />
                                    {empData.nomineeBu || empData.nomineeUnit}
                                </Col>
                            </>
                        ) : null}
                        <Col span={8}>
                            <Text strong>Nominator</Text> <br />
                            {empData.nominatorName}
                        </Col>
                    </Row>
                    <Divider />
                    <div style={{ marginBottom: 16 }}>
                        <Text strong>Nominator's Citation:</Text>
                        <div>
                            {empData.formFields
                                .split('\n\n---\n\n')
                                .map((block: string, idx: number) => {
                                    const [nominatorLine, ...citationLines] = block.split('\n');
                                    return (
                                        <div key={idx} style={{ marginBottom: 12 }}>
                                            <Text strong style={{ display: 'block' }}>
                                                {nominatorLine.replace(/^Nominator:/, '')}
                                            </Text>
                                            <Paragraph style={{ marginBottom: 0 }}>
                                                {citationLines.join('\n')}
                                            </Paragraph>
                                        </div>
                                    );
                                })}
                        </div>
                    </div>
                    <Divider />
                    <div style={{ marginBottom: 16 }}>
                        <ul style={{ marginTop: 8, marginBottom: 0 }}>
                            {parsedData.map((section, index) => (
                                <li key={index}>
                                    <strong>{section[0]}</strong>
                                    <ul>
                                        {section[1].map((item: string, subIndex: any) => (
                                            <li key={subIndex}>{item}</li>
                                        ))}
                                    </ul>
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            ) : (
                <div>Loading...</div>
            )}
        </Modal>
    );
};

export default NomineeDetailsModal;
